import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import type { AdminFeature } from '../../types/admin';
import { adminFeatureApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { BugHistoryIcon } from '../../components/BugHistoryIcon';
import { Can } from '../../auth/Can';
import { ConfirmDialog } from './ConfirmDialog';
import { FeatureImportDialog } from './FeatureImportDialog';
import { FeatureFormDialog } from './FeatureFormDialog';
import { FeatureImagesDialog } from './FeatureImagesDialog';
import { FEATURE_STATUS_META } from './statusMeta';

export function FeatureManagement(): JSX.Element {
  const { projectId, phaseId } = useParams<{ projectId: string; phaseId: string }>();
  const navigate = useNavigate();
  const [features, setFeatures] = useState<AdminFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formFeature, setFormFeature] = useState<AdminFeature | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminFeature | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [imagesFeature, setImagesFeature] = useState<AdminFeature | null>(null);

  const load = useCallback(async () => {
    if (!phaseId) return;
    setLoading(true);
    try {
      const { items: list } = await adminFeatureApi.listByPhase(phaseId, { limit: 200 });
      setFeatures([...list].sort((a, b) => a.order_index - b.order_index));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách tính năng'), 'error');
    } finally {
      setLoading(false);
    }
  }, [phaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setFormMode('create');
    setFormFeature(null);
    setFormOpen(true);
  };

  const openEdit = (feature: AdminFeature) => {
    setFormMode('edit');
    setFormFeature(feature);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminFeatureApi.remove(deleteTarget.id);
      notify('Đã xóa mềm tính năng', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa tính năng'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" onClick={() => navigate('/admin/projects')}>
          Dự án
        </Link>
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate(`/admin/projects/${projectId}`)}
        >
          Giai đoạn
        </Link>
        <Typography color="text.primary">Tính năng</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Quản lý tính năng
        </Typography>
        <Can permission="FEATURE_CREATE">
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
              Import JSON
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Thêm tính năng
            </Button>
          </Stack>
        </Can>
      </Stack>

      <Can
        permission="FEATURE_VIEW"
        fallback={<Alert severity="warning">Bạn không đủ quyền xem danh sách tính năng.</Alert>}
      >
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Thứ tự</TableCell>
                  <TableCell>Tên tính năng</TableCell>
                  <TableCell>Mô tả</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Yêu cầu thay đổi</TableCell>
                  <TableCell align="right">Hành động</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && features.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Chưa có tính năng nào.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  features.map((feature) => (
                    <TableRow key={feature.id} hover>
                      <TableCell>{feature.order_index}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {feature.name}
                          <BugHistoryIcon
                            totalBugCount={feature.total_bug_count}
                            openBugCount={feature.open_bug_count}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>{feature.description || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={FEATURE_STATUS_META[feature.status].label}
                          color={FEATURE_STATUS_META[feature.status].color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {feature.has_open_change_request ? (
                          <Chip label="Đang mở" color="error" size="small" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Can permission="FEATURE_IMAGE_VIEW">
                          <Tooltip title="Quản lý hình ảnh">
                            <IconButton size="small" onClick={() => setImagesFeature(feature)}>
                              <ImageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can permission="FEATURE_UPDATE">
                          <Tooltip title="Sửa">
                            <IconButton size="small" onClick={() => openEdit(feature)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can permission="FEATURE_DELETE">
                          <Tooltip title="Xóa mềm">
                            <IconButton size="small" onClick={() => setDeleteTarget(feature)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Can>

      {/* Dialog tạo / sửa tính năng */}
      {phaseId && (
        <FeatureFormDialog
          open={formOpen}
          mode={formMode}
          phaseId={phaseId}
          feature={formFeature}
          defaultOrderIndex={features.length}
          onClose={() => setFormOpen(false)}
          onSaved={load}
        />
      )}

      {/* Dialog quản lý hình ảnh */}
      <FeatureImagesDialog feature={imagesFeature} onClose={() => setImagesFeature(null)} />

      {phaseId && (
        <FeatureImportDialog
          open={importOpen}
          phaseId={phaseId}
          onClose={() => setImportOpen(false)}
          onImported={load}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm tính năng"
        message={`Tính năng "${deleteTarget?.name}" sẽ bị ẩn khỏi hệ thống (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
